// Port of TrailMap/ProviderCoverage.swift (TrailBivvyKit): bundled coverage outlines of the map providers — dissolved
// and simplified Natural Earth 1:10m country outlines (public domain), embedded in ProviderCoverageData.ts as compact
// JSON (polyline encoding, 1e-6°).
//
// Precision: land borders are simplified at 400 m, coasts at 1.5 km (FR, DE, ES), 3 km (NO) and 4.5 km (US); the
// Europe-wide outline at 3 km / 8 km. Small islands are dropped. Tile tests add `coverageMarginMeters`, provider
// selection a second pass with `CompositeMapProvider.nearCoverageMarginMeters`, so the simplification never loses a
// border or coastal area. These outlines only decide which map is shown or downloaded; they are not boundaries of any
// legal significance.

import type { DataProvenance, GeoBounds, GeoPolygon } from '../core/models';
import { boundsUnion, polygonBounds } from '../core/geo';
import { decodePolygon, type PolygonJSON } from '../core/polyline';
import { CoverageArea, polygonIsValid } from '../offline/CoverageArea';
import { encodedRegions } from './ProviderCoverageData';

/** A bundled coverage outline (raw values identical to Swift). */
export type ProviderCoverageRegion =
  /** Switzerland and Liechtenstein (swisstopo). */
  | 'CH'
  /** Metropolitan France and Corsica (overseas departments excluded). */
  | 'FR'
  /** Austria (basemap.at). */
  | 'AT'
  /** Germany (BKG TopPlusOpen, offline). */
  | 'DE'
  /** Spain including the Balearic and Canary Islands. */
  | 'ES'
  /** Mainland Norway and its coastal islands (Svalbard and Jan Mayen excluded, as in Kartverket's topo extent). */
  | 'NO'
  /** The 50 states (Aleutian islands west of 180° excluded). */
  | 'US'
  /**
   * EU, EFTA, the United Kingdom and the western Balkans (continental Europe without Russia, Belarus, Ukraine and
   * Moldova; Svalbard, the Azores, Madeira and the Canary Islands excluded).
   */
  | 'EUROPE';

/** Swift case names → raw values. */
export const ProviderCoverageRegions = {
  switzerland: 'CH',
  france: 'FR',
  austria: 'AT',
  germany: 'DE',
  spain: 'ES',
  norway: 'NO',
  unitedStates: 'US',
  europe: 'EUROPE',
} as const satisfies Record<string, ProviderCoverageRegion>;

export const allProviderCoverageRegions: readonly ProviderCoverageRegion[] = Object.values(ProviderCoverageRegions);

let decoded: Map<string, GeoPolygon[]> | undefined;
let boundsByRegion: Map<string, GeoBounds> | undefined;

/** Decoded once, on first use. An unreadable payload yields no outlines (guarded by tests). */
function decodedRegions(): Map<string, GeoPolygon[]> {
  if (decoded) return decoded;
  const result = new Map<string, GeoPolygon[]>();
  try {
    const regions = JSON.parse(encodedRegions) as Array<{ id: string; polygons: PolygonJSON[] }>;
    for (const region of regions) {
      if (result.has(region.id)) continue; // first wins, like Swift's uniquingKeysWith
      result.set(region.id, region.polygons.map(decodePolygon).filter(polygonIsValid));
    }
  } catch (error) {
    console.error('Bundled provider coverage unreadable', error);
  }
  decoded = result;
  return result;
}

function regionBounds(): Map<string, GeoBounds> {
  if (boundsByRegion) return boundsByRegion;
  const result = new Map<string, GeoBounds>();
  for (const [id, polygons] of decodedRegions()) {
    let box: GeoBounds | undefined;
    for (const polygon of polygons) {
      const b = polygonBounds(polygon);
      if (b) box = box ? boundsUnion(box, b) : b;
    }
    if (box) result.set(id, box);
  }
  boundsByRegion = result;
  return result;
}

const areaCache = new Map<ProviderCoverageRegion, CoverageArea>();

export const ProviderCoverage = {
  /** Where the outlines come from. */
  provenance: {
    provider: 'Natural Earth',
    dataset: 'Admin 0 – Countries 1:10m v5.1.2 (dissolved and simplified map-provider coverage)',
    sourceURL: 'https://www.naturalearthdata.com/downloads/10m-cultural-vectors/10m-admin-0-countries/',
    license: 'Public domain',
    attribution: 'Made with Natural Earth',
    fetchedAt: 1_790_553_600_000, // 2026-09-28
    isSynthetic: false,
    licenseReviewStatus: 'pendingHumanReview',
  } as DataProvenance,

  /** The outline polygons of `region` (never empty unless the bundled data is corrupt — guarded by tests). */
  polygons(region: ProviderCoverageRegion): GeoPolygon[] {
    return decodedRegions().get(region) ?? [];
  },

  /** Bounding box of `region`'s outline (a zero box at 0,0 if the bundled data were unreadable). */
  bounds(region: ProviderCoverageRegion): GeoBounds {
    return regionBounds().get(region) ?? { minLatitude: 0, minLongitude: 0, maxLatitude: 0, maxLongitude: 0 };
  },

  /** The coverage area of `region` (box + polygons), built once. */
  area(region: ProviderCoverageRegion): CoverageArea {
    let area = areaCache.get(region);
    if (!area) {
      area = new CoverageArea(ProviderCoverage.bounds(region), ProviderCoverage.polygons(region));
      areaCache.set(region, area);
    }
    return area;
  },
};
